import { useEffect, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Box,
  Typography,
  Paper,
  Stack,
  TextField,
  MenuItem,
  Autocomplete,
  Button,
  Alert,
  Divider,
  LinearProgress,
  CircularProgress,
  Chip,
} from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import WifiTetheringIcon from '@mui/icons-material/WifiTethering';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { useMe } from '../api/hooks.js';
import { api } from '../api/client.js';
import { useT } from '../i18n/useT.js';

type Provider = 'anthropic' | 'openai' | 'none';

interface AiSettings {
  provider: Provider;
  model: string;
  baseUrl: string;
  language: string;
  hasApiKey: boolean;
  active: boolean;
  persistedKeys: string[];
  models: { anthropic: string[]; openai: string[] };
}

const PROVIDER_LABEL: Record<Provider, string> = {
  anthropic: 'Anthropic (Claude)',
  openai: 'OpenAI / compatible',
  none: 'Disabled (use built-in heuristic)',
};

// Curated languages for AI summaries. The value is the English language name
// (fed to the prompt); the label shows the native name for recognisability.
const LANGUAGES: Array<{ value: string; label: string }> = [
  { value: 'auto', label: 'Auto (model default)' },
  { value: 'English', label: 'English' },
  { value: 'Turkish', label: 'Türkçe (Turkish)' },
  { value: 'Spanish', label: 'Español (Spanish)' },
  { value: 'German', label: 'Deutsch (German)' },
  { value: 'French', label: 'Français (French)' },
  { value: 'Italian', label: 'Italiano (Italian)' },
  { value: 'Portuguese', label: 'Português (Portuguese)' },
  { value: 'Dutch', label: 'Nederlands (Dutch)' },
  { value: 'Russian', label: 'Русский (Russian)' },
  { value: 'Arabic', label: 'العربية (Arabic)' },
  { value: 'Japanese', label: '日本語 (Japanese)' },
  { value: 'Chinese', label: '中文 (Chinese)' },
];

export function AiSettingsPage() {
  const t = useT().ai;
  const { data: me } = useMe();
  const orgId = me?.organizations[0]?.id;
  const isAdmin = ['OWNER', 'ADMIN'].includes(me?.organizations[0]?.role ?? '');
  const base = `/v1/orgs/${orgId}/ai`;
  const qc = useQueryClient();

  const cfg = useQuery({
    enabled: !!orgId && isAdmin,
    queryKey: ['ai-settings', orgId],
    queryFn: async () => (await api.get(`${base}/settings`)).data as AiSettings,
  });

  const EMPTY = { provider: 'anthropic' as Provider, model: '', apiKey: '', baseUrl: '', language: 'auto' };
  const [form, setForm] = useState(EMPTY);
  const [saved, setSaved] = useState(EMPTY);

  const hydrate = (d: AiSettings) => {
    const next = { provider: d.provider, model: d.model, apiKey: '', baseUrl: d.baseUrl, language: d.language || 'auto' };
    setForm(next);
    setSaved(next);
  };
  useEffect(() => {
    if (cfg.data) hydrate(cfg.data);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cfg.data]);

  const dirty = JSON.stringify(form) !== JSON.stringify(saved);
  const set = <K extends keyof typeof form>(k: K, v: (typeof form)[K]) => setForm((f) => ({ ...f, [k]: v }));

  const save = useMutation({
    mutationFn: async () => (await api.put(`${base}/settings`, form)).data as AiSettings,
    onSuccess: (data) => {
      hydrate(data);
      qc.setQueryData(['ai-settings', orgId], data);
    },
  });

  // Models fetched live from the provider using the entered (or stored) key.
  const [models, setModels] = useState<string[]>([]);
  const [loadingModels, setLoadingModels] = useState(false);
  const [modelsNote, setModelsNote] = useState('');
  const loadModels = async (provider: Provider, apiKey: string, baseUrl: string) => {
    if (provider === 'none') { setModels([]); setModelsNote(''); return; }
    setLoadingModels(true);
    try {
      const res = (await api.post(`${base}/models`, { provider, apiKey, baseUrl })).data as { models: string[] };
      setModels(res.models);
      setModelsNote(res.models.length ? t.modelsAvailable(res.models.length) : t.modelsFailed);
    } catch {
      setModels([]);
      setModelsNote(t.modelsFailedManual);
    } finally {
      setLoadingModels(false);
    }
  };
  // On load: if a key is already stored, fetch its models via the backend.
  useEffect(() => {
    if (cfg.data?.hasApiKey && cfg.data.provider !== 'none') {
      loadModels(cfg.data.provider, '', cfg.data.baseUrl);
    } else {
      setModels([]);
      setModelsNote('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cfg.data]);

  // Live end-to-end test with the current (unsaved) values.
  const [testing, setTesting] = useState(false);
  const [result, setResult] = useState<{ ok: boolean; message: string } | null>(null);
  const runTest = async () => {
    setTesting(true);
    setResult(null);
    try {
      const res = (await api.post(`${base}/test`, form)).data as { ok: boolean; message: string };
      setResult(res);
    } catch (e) {
      const msg = (e as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error?.message ?? t.testFailed;
      setResult({ ok: false, message: msg });
    } finally {
      setTesting(false);
    }
  };

  if (!isAdmin) {
    return (
      <Box>
        <Typography variant="h5" sx={{ mb: 2 }}>{t.title}</Typography>
        <Alert severity="info">{t.needAdmin}</Alert>
      </Box>
    );
  }

  const hasPersisted = (cfg.data?.persistedKeys.length ?? 0) > 0;
  const presetModels = form.provider === 'openai' ? cfg.data?.models.openai ?? [] : cfg.data?.models.anthropic ?? [];
  // Prefer the provider's live model list once we have it; else the presets.
  const modelOptions = models.length ? models : presetModels;
  const isOpenAi = form.provider === 'openai';
  const isNone = form.provider === 'none';

  return (
    <Box sx={{ maxWidth: 720 }}>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
        <AutoAwesomeIcon color="primary" />
        <Typography variant="h5">{t.title}</Typography>
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {t.subtitle}
      </Typography>

      {cfg.isLoading && <LinearProgress sx={{ mb: 2 }} />}

      {cfg.data && (
        <Alert severity={cfg.data.active ? 'success' : 'info'} sx={{ mb: 2 }}>
          {cfg.data.active
            ? t.on(t.providers[cfg.data.provider] ?? cfg.data.provider, cfg.data.model)
            : t.off}
        </Alert>
      )}

      <Paper sx={{ p: 2.5, mb: 2 }}>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ color: 'primary.main', mb: 0.5 }}>
          <AutoAwesomeIcon />
          <Typography variant="subtitle1" fontWeight={700} color="text.primary">{t.section}</Typography>
        </Stack>
        <Typography variant="caption" color="text.secondary">
          {t.sectionHint}
        </Typography>
        <Divider sx={{ my: 1.5 }} />

        <Stack spacing={2}>
          <TextField
            select
            fullWidth
            label={t.provider}
            value={form.provider}
            onChange={(e) => {
              const p = e.target.value as Provider;
              set('provider', p);
              setModels([]);
              setModelsNote('');
              // Refetch models for the new provider if we already have a key.
              if (p !== 'none' && (form.apiKey.trim() || cfg.data?.hasApiKey)) {
                loadModels(p, form.apiKey, form.baseUrl);
              }
            }}
          >
            {(['anthropic', 'openai', 'none'] as Provider[]).map((p) => (
              <MenuItem key={p} value={p}>{t.providers[p] ?? PROVIDER_LABEL[p]}</MenuItem>
            ))}
          </TextField>

          <TextField
            select
            fullWidth
            label={t.language}
            value={form.language}
            onChange={(e) => set('language', e.target.value)}
            helperText={
              isNone
                ? t.languageHelpNone
                : t.languageHelp
            }
          >
            {LANGUAGES.map((l) => (
              <MenuItem key={l.value} value={l.value}>
                {l.value === 'auto' ? t.languageAuto : l.label}
              </MenuItem>
            ))}
          </TextField>

          {!isNone && (
            <>
              <TextField
                fullWidth
                type="password"
                label={t.apiKey}
                value={form.apiKey}
                onChange={(e) => set('apiKey', e.target.value)}
                onBlur={() => { if (form.apiKey.trim() || cfg.data?.hasApiKey) loadModels(form.provider, form.apiKey, form.baseUrl); }}
                placeholder={cfg.data?.hasApiKey ? '•••••••• (configured)' : isOpenAi ? 'sk-…' : 'sk-ant-…'}
                helperText={t.apiKeyHelp}
              />

              <Autocomplete
                freeSolo
                loading={loadingModels}
                options={modelOptions}
                value={form.model}
                onInputChange={(_, v) => set('model', v)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={t.model}
                    placeholder={isOpenAi ? 'gpt-4o-mini' : 'claude-opus-5-5'}
                    helperText={loadingModels ? t.loadingModels : modelsNote || t.modelHelp}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {loadingModels ? <CircularProgress color="inherit" size={16} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />

              {isOpenAi && (
                <TextField
                  fullWidth
                  label={t.baseUrl}
                  value={form.baseUrl}
                  onChange={(e) => set('baseUrl', e.target.value)}
                  onBlur={() => { if (form.apiKey.trim() || cfg.data?.hasApiKey || form.baseUrl.trim()) loadModels(form.provider, form.apiKey, form.baseUrl); }}
                  placeholder="https://api.openai.com/v1"
                  helperText={t.baseUrlHelp}
                />
              )}

              <Stack direction="row" spacing={1.5} alignItems="center">
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={testing ? <CircularProgress size={14} /> : <WifiTetheringIcon />}
                  disabled={testing}
                  onClick={runTest}
                >
                  {testing ? t.testing : t.testConnection}
                </Button>
                {result && (
                  <Stack direction="row" spacing={0.5} alignItems="center" sx={{ color: result.ok ? 'success.main' : 'error.main', minWidth: 0 }}>
                    {result.ok ? <CheckCircleIcon fontSize="small" /> : <ErrorOutlineIcon fontSize="small" />}
                    <Typography variant="caption" sx={{ wordBreak: 'break-word' }}>{result.message}</Typography>
                  </Stack>
                )}
              </Stack>
            </>
          )}

          {isNone && (
            <Alert severity="info" variant="outlined">
              {t.disabledNote}
            </Alert>
          )}
        </Stack>
      </Paper>

      {/* Sticky action bar */}
      <Paper
        elevation={3}
        sx={{
          position: 'sticky', bottom: 16, mt: 2, p: 1.75, borderRadius: 2,
          border: '1px solid', borderColor: dirty ? 'primary.main' : 'divider',
          display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', zIndex: 2,
        }}
      >
        <Button variant="contained" disabled={save.isPending || !dirty} onClick={() => save.mutate()}>
          {save.isPending ? t.saving : hasPersisted ? t.update : t.save}
        </Button>
        <Button variant="text" color="inherit" disabled={!dirty || save.isPending} onClick={() => cfg.data && hydrate(cfg.data)}>
          {t.discard}
        </Button>
        <Box sx={{ flex: 1 }} />
        {save.isError ? (
          <Typography variant="body2" color="error">{t.saveFailed}</Typography>
        ) : dirty ? (
          <Typography variant="body2" color="warning.main">{t.unsaved}</Typography>
        ) : save.isSuccess ? (
          <Chip size="small" color="success" label={t.savedApplied} />
        ) : hasPersisted ? (
          <Typography variant="body2" color="text.secondary">{t.allSaved}</Typography>
        ) : null}
      </Paper>
    </Box>
  );
}
